import { useState, useEffect, useRef } from 'react'
import { Table, Card, Tag, Button, Space, Modal, Form, Input, Select, message, Upload } from 'antd'
import { CheckOutlined, UploadOutlined, DownloadOutlined, FileOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd'
import { invoiceApi } from '../services/api'
import axios from 'axios'

const statusMap: Record<number, { label: string; color: string }> = {
  1: { label: '待审核', color: 'orange' },
  2: { label: '待开票', color: 'blue' },
  3: { label: '已开票', color: 'green' },
  4: { label: '已拒绝', color: 'red' },
  5: { label: '已作废', color: 'default' }
}

const typeMap: Record<number, string> = { 1: '普通发票', 2: '增值税专用发票' }

export default function Invoices() {
  const [loading, setLoading] = useState(false)
  const [list, setList] = useState<any[]>([])
  const [detail, setDetail] = useState<any>(null)
  const [handleModalOpen, setHandleModalOpen] = useState(false)
  const [handleLoading, setHandleLoading] = useState(false)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [form] = Form.useForm()
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([])
  const fileInputRef = useRef<any>(null)

  useEffect(() => { loadData() }, [statusFilter])

  const loadData = async () => {
    setLoading(true)
    try {
      const params: any = {}
      if (statusFilter !== undefined) params.status = statusFilter
      const res: any = await invoiceApi.getList(params)
      setList(res.data?.list || [])
    } catch { message.error('加载失败') }
    finally { setLoading(false) }
  }

  const openDetail = async (id: number) => {
    setUploadFileList([])
    const res: any = await invoiceApi.getDetail(id)
    setDetail(res.data)
  }

  const handleInvoice = (record: any) => {
    setSelectedItem(record)
    form.setFieldsValue({ status: 2 })
    setHandleModalOpen(true)
  }

  const submitHandle = async () => {
    try {
      const values = await form.validateFields()
      setHandleLoading(true)
      await invoiceApi.handle(selectedItem.id, values)
      message.success('处理成功')
      setHandleModalOpen(false)
      loadData()
    } catch (err: any) {
      if (!err.message?.includes('validate')) {
        message.error(err?.data?.message || '操作失败')
      }
    } finally {
      setHandleLoading(false)
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const isValid = file.type === 'application/pdf' || ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)
    if (!isValid) { message.error('只支持 PDF/JPG/PNG 格式'); return }
    if (file.size / 1024 / 1024 > 10) { message.error('文件不能超过 10MB'); return }
    setUploadLoading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      if (detail?.id) form.append('invoice_id', detail.id)
      const res: any = await axios.post('/api/invoices/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      })
      if (res.data.code === 200) {
        message.success('上传成功，已自动保存')
        openDetail(detail.id) // 立即刷新发票详情
        setUploadFileList([]) // 清空文件列表
      } else {
        message.error(res.data.message || '上传失败')
        setUploadFileList([])
      }
    } catch { message.error('上传失败'); setUploadFileList([]) }
    finally { setUploadLoading(false); if (fileInputRef.current) fileInputRef.current.value = '' }
  }

  const handleUploadSave = async () => {
    if (uploadFileList.length === 0 || !uploadFileList[0].response?.data?.file_path) {
      message.error('请先上传文件')
      return
    }
    if (!detail) return
    try {
      await invoiceApi.saveFile(detail.id, uploadFileList[0].response.data.file_path)
      message.success('发票文件已保存')
      openDetail(detail.id)
    } catch { message.error('保存失败') }
  }

  const columns = [
    { title: '发票编号', dataIndex: 'invoice_no', key: 'invoice_no' },
    { title: '用户', dataIndex: 'user_phone', key: 'user_phone' },
    { title: '企业客户', dataIndex: 'enterprise_name', key: 'enterprise_name' },
    { title: '发票抬头', dataIndex: 'title', key: 'title' },
    { title: '发票类型', dataIndex: 'type', key: 'type', render: (v: number) => typeMap[v] || v },
    { title: '开票金额', dataIndex: 'total_amount', key: 'total_amount', render: (v: number) => `¥${v}` },
    { title: '订单数', dataIndex: 'order_count', key: 'order_count' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (v: number) => {
        const s = statusMap[v] || statusMap[1]
        return <Tag color={s.color}>{s.label}</Tag>
      }
    },
    { title: '申请时间', dataIndex: 'created_at', key: 'created_at', render: (v: string) => new Date(v).toLocaleString() },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => openDetail(record.id)}>详情</Button>
          {record.status === 1 && (
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleInvoice(record)}>处理</Button>
          )}
          {record.status === 2 && (
            <Button size="small" type="primary" onClick={() => {
              setSelectedItem(record)
              form.setFieldsValue({ status: 3 })
              setHandleModalOpen(true)
            }}>已开票</Button>
          )}
        </Space>
      )
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>发票管理</h2>
        <Select placeholder="筛选状态" allowClear style={{ width: 120 }}
          onChange={(v) => setStatusFilter(v)}>
          {Object.entries(statusMap).map(([k, v]) => (
            <Select.Option key={k} value={parseInt(k)}>{v.label}</Select.Option>
          ))}
        </Select>
      </div>

      <Card>
        <Table dataSource={list} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="发票详情"
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width={560}
      >
        {detail && (
          <div style={{ lineHeight: 2.2 }}>
            <div style={{ marginBottom: 12 }}>
              <strong>发票编号：</strong>{detail.invoice_no}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>用户手机：</strong>{detail.user_phone}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>发票类型：</strong>{typeMap[detail.type] || detail.type}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>发票抬头：</strong>{detail.title}
            </div>
            {detail.tax_no && <div style={{ marginBottom: 12 }}><strong>税号：</strong>{detail.tax_no}</div>}
            {detail.address && <div style={{ marginBottom: 12 }}><strong>地址：</strong>{detail.address}</div>}
            {detail.bank && <div style={{ marginBottom: 12 }}><strong>银行：</strong>{detail.bank} {detail.bank_account}</div>}
            {detail.email && <div style={{ marginBottom: 12 }}><strong>接收邮箱：</strong>{detail.email}</div>}
            <div style={{ marginBottom: 12 }}>
              <strong>开票金额：</strong><span style={{ color: '#ff4d4f', fontWeight: 700, fontSize: 16 }}>¥{detail.total_amount}</span>
            </div>
            {detail.audit_remark && <div style={{ marginBottom: 12 }}><strong>审核备注：</strong>{detail.audit_remark}</div>}
            {detail.invoice_no_issued && <div style={{ marginBottom: 12 }}><strong>实际发票号：</strong>{detail.invoice_no_issued}</div>}
            <div style={{ marginBottom: 12 }}>
              <strong>状态：</strong>
              <Tag color={statusMap[detail.status]?.color}>{statusMap[detail.status]?.label}</Tag>
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>申请时间：</strong>{new Date(detail.created_at).toLocaleString()}
            </div>

            {/* 发票文件区域 */}
            <div style={{ marginTop: 16, padding: '12px 16px', background: '#f5f5f5', borderRadius: 6 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}><FileOutlined style={{ marginRight: 6 }} />发票文件</div>

              {detail.file_path ? (
                <div>
                  <Space style={{ marginBottom: 8 }}>
                    <FileOutlined style={{ color: '#52c41a' }} />
                    <span>已有发票文件</span>
                    <a href={invoiceApi.getFileUrl(detail.file_path)} target="_blank" rel="noopener noreferrer">
                      <Button type="link" icon={<DownloadOutlined />} size="small">查看/下载</Button>
                    </a>
                  </Space>
                  <div style={{ fontSize: 12, color: '#999' }}>如需替换，请上传新文件并保存</div>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#666' }}>暂无发票文件，上传后可供用户查看下载</div>
              )}

              <div style={{ marginTop: 12 }}>
                <Button icon={<UploadOutlined />} loading={uploadLoading} size="small"
                  onClick={() => fileInputRef.current?.click()}>
                  上传发票文件
                </Button>
                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileSelect} />
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 处理弹窗 */}
      <Modal
        title="发票处理"
        open={handleModalOpen}
        onOk={submitHandle}
        onCancel={() => setHandleModalOpen(false)}
        confirmLoading={handleLoading}
        okText="确认"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="status" label="处理结果" rules={[{ required: true }]}>
            <Select>
              <Select.Option value={2}>通过（待开票）</Select.Option>
              <Select.Option value={3}>已开票</Select.Option>
              <Select.Option value={4}>拒绝</Select.Option>
              <Select.Option value={5}>作废</Select.Option>
            </Select>
          </Form.Item>
          {(form.getFieldValue('status') === 2 || form.getFieldValue('status') === 3) && (
            <Form.Item name="invoice_no_issued" label="实际发票号">
              <Input placeholder="请输入实际发票号码" />
            </Form.Item>
          )}
          <Form.Item name="audit_remark" label="处理备注">
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
